import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, X, Warning, Clock, User, Package, FileText, ArrowRight, UploadSimple, Trash, CaretDown, CaretRight, ChatText, PencilSimple } from "@phosphor-icons/react";
import { useAuth, hasRole, hasCapability } from "@/context/AuthContext";
import { uploadPhoto } from "@/lib/uploadPhoto";
import { EQUIPMENT_TYPES, TYPE_MAP } from "@/components/patients/equipmentTypes";
import EditPatientDialog from "@/components/patients/EditPatientDialog";
import AccessoryChips, { accessoryItems } from "@/components/AccessoryChips";
import PatientSpecFields, { patientSpecValues } from "@/components/patients/PatientSpecFields";
import PatientIssueBlock, { patientIssue } from "@/components/patients/PatientIssueBlock";
import PatientCard from "@/components/patients/PatientCard";
import Fold from "@/components/Fold";
import { useSpecSchemas } from "@/lib/specSchemas";

/**
 * Hospital de Equipos (nuevo). Fase 1: recepción, expediente, transiciones
 * de estado, timeline inmutable. Coexiste con el módulo "Hospital" antiguo
 * (que sigue en /hospital para reparaciones de garantía).
 */

const STATE_COLORS = {
  recepcion:             { bg: "#eff6ff", fg: "#1e40af", border: "#bfdbfe" },
  para_pedidos:          { bg: "#ffedd5", fg: "#9a3412", border: "#fed7aa" },
  esperando_refacciones: { bg: "#fef3c7", fg: "#78350f", border: "#fde68a" },
  segunda_revision:      { bg: "#e0e7ff", fg: "#3730a3", border: "#c7d2fe" },
  taller_externo:        { bg: "#ede9fe", fg: "#5b21b6", border: "#ddd6fe" },
  preinventario:         { bg: "#ecfeff", fg: "#155e75", border: "#a5f3fc" },
  en_inventario:         { bg: "#dcfce7", fg: "#14532d", border: "#86efac" },
  vendido:               { bg: "#f0fdf4", fg: "#052e16", border: "#22c55e" },
  donadora:              { bg: "#fce7f3", fg: "#831843", border: "#f9a8d4" },
  baja_definitiva:       { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" },
};

function StateBadge({ state, label }) {
  const c = STATE_COLORS[state] || { bg: "#f4f4f5", fg: "#3f3f46", border: "#e4e4e7" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold border"
      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
    >
      {label}
    </span>
  );
}

export default function Patients() {
  const { user } = useAuth();
  const specSchemas = useSpecSchemas();
  const canReceive = hasRole(user, "super_admin") || hasRole(user, "inventory") || hasRole(user, "technician") || hasRole(user, "taller");
  const canPreinventory = hasRole(user, "super_admin") || hasRole(user, "inventory") || hasCapability(user, "manage_preinventory");
  const [states, setStates] = useState([]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const dragRef = useRef(null);

  const stateLabel = useMemo(() => Object.fromEntries(states.map((s) => [s.key, s.label])), [states]);
  // Columnas del Kanban: estados NO terminales que sí son columna (Preinventario se trabaja en su propio módulo).
  const kanbanCols = useMemo(() => states.filter((s) => !s.terminal && s.kanban !== false), [states]);

  const reload = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [listR, statsR] = await Promise.all([
      api.get("/patients", { params }),
      api.get("/patients/stats"),
    ]);
    setItems(listR.data);
    setStats(statsR.data);
  };

  useEffect(() => {
    api.get("/patients/states").then((r) => setStates(r.data)).catch(() => {});
  }, []);

  // Deep-link: si venimos de "Mi Tablero" con ?patient=<id>, abrir detalle.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get("patient");
    if (!pid) return;
    (async () => {
      try {
        const { data } = await api.get(`/patients/${pid}`);
        setDetailFor(data);
      } catch { /* ignore */ }
      const next = new URLSearchParams(searchParams);
      next.delete("patient");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Agrupa pacientes por estado, ignorando los terminales (sólo se muestran en detalle).
  // Pacientes con estados legados (que ya fueron eliminados) aparecen en la
  // primera columna para que puedan re-clasificarse.
  const byState = useMemo(() => {
    const m = {};
    for (const c of kanbanCols) m[c.key] = [];
    const validKeys = new Set(states.map((s) => s.key));
    const firstCol = kanbanCols[0]?.key;
    for (const p of items) {
      if (m[p.current_state]) {
        m[p.current_state].push(p);
      } else if (!validKeys.has(p.current_state) && firstCol) {
        m[firstCol].push(p);
      }
    }
    return m;
  }, [items, kanbanCols, states]);

  const handleDrop = async (e, toState) => {
    e.preventDefault();
    setOverCol(null);
    const pid = dragRef.current;
    dragRef.current = null;
    if (!pid) return;
    const patient = items.find((p) => p.id === pid);
    if (!patient || patient.current_state === toState) return;
    try {
      await api.post(`/patients/${pid}/state`, { to_state: toState });
      toast.success(`Movido a ${stateLabel[toState] || toState}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo mover");
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">HOSPITAL INTERNO</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Package size={32} weight="duotone" className="text-[#002FA7]" />
            Hospital Interno
          </h1>
          <p className="text-zinc-500 mt-1 text-sm max-w-2xl">
            Expediente de cada equipo desde que llega hasta que se vende o se da de baja. Toca una tarjeta para ver todo su historial.
          </p>
        </div>
        {canReceive && (
          <Button
            onClick={() => setOpenNew(true)}
            className="bg-[#002FA7] hover:bg-[#0043c2] gap-2"
            data-testid="new-patient-btn"
          >
            <Plus size={16} weight="bold" /> Nuevo paciente
          </Button>
        )}
      </header>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="patients-stats">
          <StatChip label="Total pacientes" value={stats.total} highlight />
          <StatChip label="Recepción" value={stats.by_state.recepcion || 0} state="recepcion" />
          <StatChip label="Para pedidos" value={stats.by_state.para_pedidos || 0} state="para_pedidos" />
          <StatChip label="Esperando refacc." value={stats.by_state.esperando_refacciones || 0} state="esperando_refacciones" />
          <StatChip label="Segunda revisión" value={stats.by_state.segunda_revision || 0} state="segunda_revision" />
          <StatChip label="Taller externo" value={stats.by_state.taller_externo || 0} state="taller_externo" />
          <StatChip label="Preinventario" value={stats.by_state.preinventario || 0} state="preinventario" to={canPreinventory ? "/preinventario" : undefined} />
          <StatChip label="En inventario" value={stats.by_state.en_inventario || 0} state="en_inventario" />
        </div>
      )}

      {/* Search */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por PAT-XXX, marca, modelo, serie…" className="pl-8 pr-8" data-testid="patient-search" />
          {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"><X size={14} /></button>}
        </div>
        <span className="text-[11px] text-zinc-400">Toca una tarjeta para ver el expediente · arrástrala para cambiar de estado</span>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="patients-kanban">
        {kanbanCols.map((col) => {
          const cards = byState[col.key] || [];
          const cc = STATE_COLORS[col.key] || {};
          return (
            <div
              key={col.key}
              onDragEnter={() => setOverCol(col.key)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDragLeave={() => setOverCol((v) => (v === col.key ? null : v))}
              onDrop={(e) => handleDrop(e, col.key)}
              className={`flex flex-col rounded-lg border min-h-[300px] transition-colors ${overCol === col.key ? "border-[#002FA7] border-dashed bg-[#002FA7]/5" : "border-zinc-200 bg-zinc-50/60"}`}
              data-testid={`patient-col-${col.key}`}
            >
              <div className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cc.fg || "#6b7280" }} />
                  <div className="text-xs font-heading font-bold uppercase tracking-wider truncate">{col.label}</div>
                </div>
                <span className="text-xs font-mono-data text-zinc-500 bg-white border border-zinc-200 rounded-full px-1.5 shrink-0" data-testid={`patient-col-count-${col.key}`}>{cards.length}</span>
              </div>
              <div className="px-2 pb-2 space-y-2 flex-1 max-h-[70vh] overflow-y-auto">
                {cards.length === 0 && (
                  <div className="text-center text-[11px] text-zinc-400 py-10 italic">Sin equipos</div>
                )}
                {cards.map((p) => (
                  <PatientCard key={p.id} patient={p} onOpen={setDetailFor} dragRef={dragRef} onDragEnd={() => { dragRef.current = null; setOverCol(null); }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <NewPatientDialog open={openNew} onOpenChange={setOpenNew} onCreated={reload} />
      <PatientDetailDialog patient={detailFor} states={states} onClose={() => setDetailFor(null)} onChanged={reload} />
    </div>
  );
}

function StatChip({ label, value, state, highlight, to }) {
  const c = state ? STATE_COLORS[state] : null;
  const Tag = to ? Link : "div";
  return (
    <Tag
      {...(to ? { to, "data-testid": `stat-chip-${state}` } : {})}
      className={`flex items-baseline justify-between gap-2 px-3 py-2 rounded-md border bg-white border-zinc-200 ${highlight ? "border-[#002FA7]/40" : ""} ${to ? "hover:shadow-sm hover:border-[#002FA7]/50 transition-[box-shadow,border-color]" : ""}`}
      style={c && !highlight ? { borderLeftWidth: 3, borderLeftColor: c.fg } : highlight ? { borderLeftWidth: 3, borderLeftColor: "#002FA7" } : undefined}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{label}{to ? " ↗" : ""}</div>
      <div
        className={`font-heading text-xl font-extrabold numeric shrink-0 ${highlight ? "text-[#002FA7]" : "text-zinc-900"}`}
        style={c && !highlight ? { color: c.fg } : undefined}
      >
        {value || 0}
      </div>
    </Tag>
  );
}


// ─────────────────────── New patient dialog ───────────────────────────────
function NewPatientDialog({ open, onOpenChange, onCreated }) {
  const empty = {
    batch_number: "",
    equipment_type: "laptop",
    equipment_subtype: "",
    brand: "", model: "",
    processor: "", ram: "", storage: "", color: "",
    aesthetic_condition: "", includes_charger: false, includes_battery: false,
    issue: "", observations: "", photos: [], damages: [],
    specs: {},
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reset = () => setForm(empty);
  const close = (v) => { if (!v) reset(); onOpenChange(v); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const currentType = TYPE_MAP[form.equipment_type] || TYPE_MAP.laptop;
  // Cuando cambia el tipo, limpiamos subtipo y specs anteriores para que no
  // queden campos huérfanos de la categoría anterior.
  const changeType = (v) => setForm((f) => ({
    ...f, equipment_type: v, equipment_subtype: "", specs: {},
    // limpia campos base que no aplican al nuevo tipo
    processor: TYPE_MAP[v]?.baseFields.includes("processor") ? f.processor : "",
    ram: TYPE_MAP[v]?.baseFields.includes("ram") ? f.ram : "",
    storage: TYPE_MAP[v]?.baseFields.includes("storage") ? f.storage : "",
    includes_charger: TYPE_MAP[v]?.charger ? f.includes_charger : false,
    includes_battery: TYPE_MAP[v]?.battery ? f.includes_battery : false,
  }));

  const addDamage = () => set("damages", [...form.damages, { kind: "golpe", description: "" }]);
  const updDamage = (idx, key, val) => set("damages", form.damages.map((d, i) => i === idx ? { ...d, [key]: val } : d));
  const rmDamage = (idx) => set("damages", form.damages.filter((_, i) => i !== idx));

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // permite re-seleccionar la misma foto
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        try {
          const url = await uploadPhoto(f, { folder: "alendrapc/patients" });
          uploaded.push(url);
        } catch (err) {
          toast.error(err.message || "Error subiendo foto");
        }
      }
      if (uploaded.length) {
        setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
        toast.success(`${uploaded.length} foto(s) agregadas`);
      }
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.brand.trim() || !form.model.trim()) {
      toast.error("Marca y modelo son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aesthetic_condition: form.aesthetic_condition || null,
      };
      const { data } = await api.post("/patients", payload);
      toast.success(`Paciente ${data.code} recibido`);
      close(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el paciente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={20} weight="bold" className="text-[#002FA7]" />
            Nuevo paciente — recepción de equipo
          </DialogTitle>
          <DialogDescription className="sr-only">Formulario de recepción: motivo de ingreso, datos del equipo, fotos, daños y observaciones.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Section title="Categoría del equipo">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo *">
                <Select value={form.equipment_type} onValueChange={changeType}>
                  <SelectTrigger data-testid="pat-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {currentType.subtypes && (
                <Field label="Subtipo">
                  <Select value={form.equipment_subtype || "none"} onValueChange={(v) => set("equipment_subtype", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="pat-subtype"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {currentType.subtypes.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          </Section>
          <section className="rounded-md border border-amber-200 border-l-4 border-l-[#E0000F] bg-amber-50/60 p-3" data-testid="pat-issue-section">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 inline-flex items-center gap-1 mb-1">
              <Warning size={12} weight="fill" className="text-[#E0000F]" /> ¿Por qué ingresa? · Problema / motivo
            </div>
            <p className="text-[11px] text-zinc-600 mb-2">Es lo primero que verá el técnico en la tarjeta: la falla, lo que le falta o el motivo de la revisión.</p>
            <textarea value={form.issue} onChange={(e) => set("issue", e.target.value)} rows={3} className="w-full text-sm border border-amber-300 bg-white rounded-md p-2 focus:outline-none focus:border-[#002FA7] resize-none" placeholder="Ej: No enciende; le falta disipador y procesador; pantalla con líneas." data-testid="pat-issue" />
          </section>
          <Section title={`Datos del ${currentType.label.toLowerCase()}`}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Marca *"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pat-brand" /></Field>
              <Field label="Modelo *"><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="pat-model" /></Field>
              <Field label="Condición estética">
                <Select value={form.aesthetic_condition || "none"} onValueChange={(v) => set("aesthetic_condition", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pat-aesthetic"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin definir</SelectItem>
                    <SelectItem value="A">A — Como nuevo</SelectItem>
                    <SelectItem value="B">B — Buen estado</SelectItem>
                    <SelectItem value="C">C — Con detalles</SelectItem>
                    <SelectItem value="D">D — Muy usado</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="N° de lote"><Input value={form.batch_number} onChange={(e) => set("batch_number", e.target.value)} data-testid="pat-batch" /></Field>
            </div>
            <div className="mt-3 border border-zinc-200 rounded-md bg-white p-3">
              <PatientSpecFields form={form} set={set} setSpec={setSpec} equipmentType={form.equipment_type} testidPrefix="pat-spec" />
            </div>
            {currentType.hasGpuGroup && (
              <div className="mt-3 border border-zinc-200 rounded-md bg-zinc-50/50 p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs text-zinc-700 font-semibold">Tarjeta gráfica</Label>
                    <div className="text-[11px] text-zinc-500">¿El equipo trae GPU dedicada o integrada con memoria?</div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={!!form.specs.has_gpu}
                      onCheckedChange={(v) => {
                        setSpec("has_gpu", !!v);
                        if (!v) { setSpec("gpu_model", ""); setSpec("gpu_memory", ""); }
                      }}
                      data-testid="pat-has-gpu"
                    />
                    <span className="font-semibold">{form.specs.has_gpu ? "Sí, tiene" : "No tiene"}</span>
                  </label>
                </div>
                {form.specs.has_gpu && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Modelo / chip">
                      <Input
                        value={form.specs.gpu_model || ""}
                        onChange={(e) => setSpec("gpu_model", e.target.value)}
                        placeholder="ej. GTX 1650, RTX 3060, Radeon Vega 8, Intel UHD 620"
                        data-testid="pat-gpu-model"
                      />
                    </Field>
                    <Field label="Memoria (VRAM)">
                      <Input
                        value={form.specs.gpu_memory || ""}
                        onChange={(e) => setSpec("gpu_memory", e.target.value)}
                        placeholder="ej. 128MB, 2GB, 4GB, 12GB"
                        data-testid="pat-gpu-memory"
                      />
                    </Field>
                  </div>
                )}
              </div>
            )}
            {(currentType.charger || currentType.battery || currentType.cable) && (
              <div className="flex gap-4 mt-3 flex-wrap">
                {currentType.charger && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={form.includes_charger} onCheckedChange={(v) => set("includes_charger", !!v)} data-testid="pat-charger" /> Incluye cargador
                  </label>
                )}
                {currentType.battery && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={form.includes_battery} onCheckedChange={(v) => set("includes_battery", !!v)} data-testid="pat-battery" /> Incluye batería
                  </label>
                )}
                {currentType.cable && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={!!form.specs.includes_cable} onCheckedChange={(v) => setSpec("includes_cable", !!v)} data-testid="pat-cable" /> Incluye cable
                  </label>
                )}
              </div>
            )}
          </Section>
          <Section title="Fotos de recepción">
            <p className="text-[11px] text-zinc-500 mb-2">Suben como evidencia del estado con el que ingresó el equipo.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {form.photos.map((url, idx) => (
                <div key={idx} className="relative aspect-square border border-zinc-200 rounded-md overflow-hidden group">
                  <img src={url} alt={`recepción ${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`pat-photo-rm-${idx}`}
                    aria-label="Quitar foto"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              ))}
              <label
                className={`aspect-square border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-1 text-xs text-zinc-500 cursor-pointer hover:border-[#002FA7] hover:text-[#002FA7] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="pat-photo-upload"
              >
                <input type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden" disabled={uploading} />
                <UploadSimple size={20} weight="bold" />
                <span>{uploading ? "Subiendo..." : "Subir fotos"}</span>
              </label>
            </div>
          </Section>
          <Section title="Daños detectados">
            <div className="space-y-2">
              {form.damages.length === 0 && <p className="text-xs text-zinc-400">Sin daños registrados. Agrega uno si detectas algún golpe, rayón o faltante.</p>}
              {form.damages.map((d, idx) => (
                <div key={idx} className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
                  <Select value={d.kind} onValueChange={(v) => updDamage(idx, "kind", v)}>
                    <SelectTrigger className="h-9" data-testid={`damage-kind-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="golpe">Golpe</SelectItem>
                      <SelectItem value="rayon">Rayón</SelectItem>
                      <SelectItem value="pantalla_rota">Pantalla rota</SelectItem>
                      <SelectItem value="faltante">Faltante</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={d.description} onChange={(e) => updDamage(idx, "description", e.target.value)} placeholder="¿Dónde?" className="h-9" data-testid={`damage-desc-${idx}`} />
                  <button type="button" onClick={() => rmDamage(idx)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`damage-rm-${idx}`}><X size={14} /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addDamage} className="gap-1" data-testid="add-damage-btn">
                <Warning size={12} weight="bold" /> Agregar daño
              </Button>
            </div>
          </Section>
          <Section title="Observaciones adicionales">
            <textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} rows={2} className="w-full text-sm border border-zinc-200 rounded-md p-2 focus:outline-none focus:border-[#002FA7]" placeholder="Notas secundarias de la recepción (opcional)" data-testid="pat-obs" />
          </Section>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => close(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving || uploading} className="bg-[#002FA7] hover:bg-[#0043c2]" data-testid="pat-submit">
              {saving ? "Registrando…" : "Registrar paciente"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">{title}</div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-600">{label}</Label>
      {children}
    </div>
  );
}

// ─────────────────────── Detail dialog ────────────────────────────────────
function PatientSpecList({ patient }) {
  const { schemaFor, equipmentCategory } = useSpecSchemas();
  const fields = schemaFor(equipmentCategory(patient.equipment_type)).filter((f) => !["gpu", "includes"].includes(f.key));
  const vals = patientSpecValues(patient, fields);
  const rows = fields.filter((f) => String(vals[f.key] || "").trim());
  const req = fields.filter((f) => f.required);
  const done = req.filter((f) => String(vals[f.key] || "").trim()).length;
  if (rows.length === 0) return <div className="mt-0.5 text-sm text-zinc-400">— sin datos</div>;
  return (
    <div className="mt-0.5" data-testid="patient-detail-specs">
      <div className="text-sm text-zinc-900">{rows.slice(0, 3).map((f) => vals[f.key]).join(" · ")}</div>
      {rows.length > 3 && (
        <div className="text-[11px] text-zinc-600 mt-0.5">{rows.slice(3).map((f) => `${f.label}: ${vals[f.key]}`).join(" · ")}</div>
      )}
      {req.length > 0 && (
        <div className={`text-[10px] font-mono-data mt-0.5 ${done === req.length ? "text-emerald-700" : "text-amber-700"}`} data-testid="patient-detail-spec-progress">
          Ficha {done}/{req.length} obligatorios
        </div>
      )}
    </div>
  );
}

function PatientDetailDialog({ patient, states, onClose, onChanged }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canPreinventory = hasRole(user, "super_admin") || hasRole(user, "inventory") || hasCapability(user, "manage_preinventory");
  const canEdit = hasRole(user, "super_admin") || hasRole(user, "inventory");
  const [editOpen, setEditOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [current, setCurrent] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const stateLabel = useMemo(() => Object.fromEntries(states.map((s) => [s.key, s.label])), [states]);

  const dischargeToPreinventory = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/to-preinventory`);
      toast.success(`Ficha ${data.item.code} creada en Preinventario`);
      await refresh();
      navigate(`/preinventario?item=${data.item.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo dar de alta");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!patient) return;
    setCurrent(patient);
    setHistoryOpen(false);
    api.get(`/patients/${patient.id}/timeline`).then((r) => setTimeline(r.data)).catch(() => setTimeline([]));
  }, [patient]);

  if (!patient) return null;

  const refresh = async () => {
    const [pR, tR] = await Promise.all([
      api.get(`/patients/${patient.id}`),
      api.get(`/patients/${patient.id}/timeline`),
    ]);
    setCurrent(pR.data);
    setTimeline(tR.data);
    onChanged?.();
  };

  const transition = async (to_state) => {
    setBusy(true);
    try {
      await api.post(`/patients/${patient.id}/state`, { to_state });
      toast.success(`Estado: ${stateLabel[to_state] || to_state}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transición inválida");
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/patients/${patient.id}/notes`, { note });
      setNote("");
      await refresh();
    } finally { setBusy(false); }
  };

  const c = current || patient;
  const techNotes = timeline.filter((e) => e.kind === "note");
  const lastNote = techNotes[techNotes.length - 1] || null;
  const prevNote = techNotes[techNotes.length - 2] || null;
  const equipmentSummary = [
    c.received_by_name ? `Recibido por ${c.received_by_name}` : null,
    c.received_at ? new Date(c.received_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "2-digit" }) : null,
    c.supplier ? `Prov. ${c.supplier}` : null,
    [c.processor, c.ram, c.storage].filter(Boolean).join(" · ") || null,
    accessoryItems(c).length ? `${accessoryItems(c).length} accesorio(s)` : "sin accesorios",
    (c.labels || []).length ? `${c.labels.length} etiqueta(s)` : null,
  ].filter(Boolean).join(" · ");
  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/patients/${patient.id}/claim`);
      toast.success("Tomaste la tarjeta");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo tomar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!patient} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="patient-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[#002FA7]">{c.code}</span>
            <span>—</span>
            <span>{c.brand} {c.model}</span>
            {(c.labels || []).slice(0, 4).map((lb, i) => (
              <span key={i} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white" style={{ background: lb.color || "#52525B" }} data-testid={`detail-label-chip-${i}`}>{lb.text}</span>
            ))}
            {canEdit && !["vendido", "donadora", "baja_definitiva", "en_inventario"].includes(c.current_state) && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="ml-auto h-7 gap-1 text-xs" data-testid="edit-patient-btn">
                <PencilSimple size={13} weight="bold" /> Editar datos
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Expediente del equipo: problema, estado, bitácora y datos del equipo.</DialogDescription>
        </DialogHeader>
        {editOpen && <EditPatientDialog patient={c} onClose={() => setEditOpen(false)} onSaved={() => refresh()} />}

        <div className="space-y-4">
          {/* 1. Por qué está aquí */}
          <PatientIssueBlock
            patient={c}
            stateBadge={<StateBadge state={c.current_state} label={stateLabel[c.current_state] || c.current_state} />}
            canClaim={!["vendido", "donadora", "baja_definitiva", "en_inventario"].includes(c.current_state)}
            busy={busy}
            onClaim={claim}
            onChanged={(fresh) => { if (fresh) setCurrent(fresh); refresh(); }}
          />

          {/* 2. Cambiar estado */}
          <section data-testid="patient-state-row">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Cambiar estado</div>
            <div className="flex gap-1.5 flex-wrap">
              {states.filter((s) => (!s.terminal && s.key !== "preinventario") || s.key === c.current_state).map((s) => {
                const active = s.key === c.current_state;
                const sc = STATE_COLORS[s.key];
                return (
                  <button
                    key={s.key}
                    disabled={active || busy}
                    onClick={() => transition(s.key)}
                    data-testid={`transition-${s.key}`}
                    className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${active ? "cursor-default ring-2 ring-offset-1" : "hover:opacity-80"} disabled:opacity-50`}
                    style={{ background: sc?.bg, color: sc?.fg, borderColor: sc?.border, opacity: active ? 1 : 0.7, "--tw-ring-color": sc?.fg }}
                  >
                    {active && <span className="mr-1">●</span>}{s.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 3. Último avance + bitácora */}
          <section className="border border-[#002FA7]/25 rounded-md bg-white p-3" data-testid="patient-tracking-notes">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold inline-flex items-center gap-1">
                <ChatText size={12} weight="fill" /> Último avance · Bitácora del técnico
              </div>
              {techNotes.length > 0 && <span className="text-[10px] text-zinc-400 font-mono-data">{techNotes.length} registro{techNotes.length === 1 ? "" : "s"}</span>}
            </div>
            {lastNote ? (
              <div className="border-l-4 border-[#002FA7] bg-[#002FA7]/5 p-3 rounded-sm" data-testid="patient-last-note">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-zinc-900 inline-flex items-center gap-1"><User size={11} weight="bold" /> {lastNote.actor_name}</span>
                  <span className="numeric text-zinc-400 whitespace-nowrap text-[10px]">{new Date(lastNote.created_at).toLocaleString("es-MX")}</span>
                </div>
                <p className="text-sm text-zinc-800 whitespace-pre-line mt-1 leading-snug">{lastNote.summary}</p>
              </div>
            ) : (
              <p className="text-xs text-zinc-400 italic mb-2">Aún nadie documenta avances. Escribe qué hiciste, dónde te quedaste y qué falta.</p>
            )}
            {prevNote && (
              <div className="border-l-2 border-zinc-300 bg-zinc-50 p-2 rounded-sm text-xs mt-2" data-testid="patient-prev-note">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-zinc-700">{prevNote.actor_name}</span>
                  <span className="numeric text-zinc-400 whitespace-nowrap text-[10px]">{new Date(prevNote.created_at).toLocaleString("es-MX")}</span>
                </div>
                <p className="text-zinc-600 whitespace-pre-line mt-0.5 line-clamp-2">{prevNote.summary}</p>
              </div>
            )}
            <div className="flex gap-2 items-end mt-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Registrar avance: qué hiciste, dónde te quedaste, qué falta…"
                className="flex-1 text-sm border border-zinc-200 rounded-md p-2 focus:outline-none focus:border-[#002FA7] resize-none"
                data-testid="patient-note-input"
              />
              <Button onClick={submitNote} disabled={!note.trim() || busy} className="gap-1 bg-[#002FA7] hover:bg-[#0043c2] shrink-0" data-testid="patient-note-submit">
                <ArrowRight size={12} weight="bold" /> Registrar
              </Button>
            </div>
          </section>

          {/* 4. Datos del equipo (colapsado) */}
          <Fold title="Datos del equipo" icon={Package} summary={equipmentSummary} testid="patient-equipment-fold">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold inline-flex items-center gap-1"><User size={11} weight="bold" /> Recibido por</div>
                <div className="mt-0.5 font-semibold text-zinc-900 text-sm">{c.received_by_name || "—"}</div>
                <div className="text-[11px] text-zinc-500">{c.received_at ? new Date(c.received_at).toLocaleString("es-MX") : "—"}</div>
                {c.technician_name && c.claimed_at && (
                  <div className="text-[10px] text-zinc-500 mt-1">Técnico {c.technician_name} tomó la tarjeta el {new Date(c.claimed_at).toLocaleString("es-MX")}</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Proveedor / Lote</div>
                <div className="mt-0.5 text-sm text-zinc-900">{c.supplier || "—"}</div>
                <div className="text-[11px] text-zinc-500">{c.batch_number || "sin lote"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Ficha técnica</div>
                <PatientSpecList patient={c} />
                {c.specs?.has_gpu && (
                  <div className="text-[11px] text-zinc-700 mt-0.5">
                    <span className="font-semibold text-zinc-500 uppercase tracking-widest text-[10px] mr-1">GPU:</span>
                    {[c.specs.gpu_model, c.specs.gpu_memory].filter(Boolean).join(" · ") || "sin detalle"}
                  </div>
                )}
                <div className="text-[11px] text-zinc-500">{c.serial_number ? `S/N: ${c.serial_number}` : "sin serie"} · {c.color || "sin color"}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-2">Accesorios entregados</div>
                <AccessoryChips device={c} className="mt-1" testid="patient-detail-acc" />
                {accessoryItems(c).length === 0 && <div className="text-[11px] text-zinc-400 italic mt-0.5">Sin accesorios registrados</div>}
              </div>
            </div>

            {c.issue && c.observations && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1 inline-flex items-center gap-1">
                  <FileText size={11} weight="bold" /> Observaciones adicionales
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-line bg-zinc-50 border border-zinc-200 rounded p-2">{c.observations}</p>
              </section>
            )}

            <LabelsEditor patient={c} onChanged={refresh} />
          </Fold>

          {/* 5. Historial — contraído por defecto */}
          <section>
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="w-full flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold py-2 border-t border-zinc-200 hover:text-zinc-800 transition-colors"
              data-testid="patient-history-toggle"
            >
              <span className="inline-flex items-center gap-1">
                {historyOpen ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                <Clock size={11} weight="bold" /> Historial de movimientos ({timeline.length})
              </span>
              <span className="text-[10px] text-zinc-400 normal-case tracking-normal">
                {historyOpen ? "Ocultar" : "Mostrar todo"}
              </span>
            </button>
            {historyOpen && (
              <div className="space-y-2 max-h-64 overflow-y-auto mt-2" data-testid="patient-timeline">
                {timeline.map((e) => (
                  <div key={e.id} className="flex items-start gap-3 text-xs border-b border-zinc-100 pb-2 last:border-none">
                    <div className={`w-1 self-stretch rounded-full ${e.kind === "note" ? "bg-[#002FA7]" : "bg-zinc-300"}`} />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className={`font-semibold ${e.kind === "note" ? "text-[#002FA7]" : "text-zinc-900"}`}>{e.summary}</div>
                        <div className="numeric text-zinc-400 whitespace-nowrap">{new Date(e.created_at).toLocaleString("es-MX")}</div>
                      </div>
                      <div className="text-zinc-500 text-[11px]">por {e.actor_name}{e.ip ? ` · ${e.ip}` : ""}</div>
                    </div>
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-xs text-zinc-400">Sin eventos aún.</p>}
              </div>
            )}
          </section>

          {/* 6. Acción secundaria: alta a Preinventario */}
          {canPreinventory && (
            <div className="border-t border-zinc-200 pt-3 flex items-center justify-between gap-3 flex-wrap" data-testid="preinventory-block">
              <div className="text-xs text-zinc-600 min-w-0">
                <span className="text-[10px] uppercase tracking-widest font-semibold mr-2" style={{ color: STATE_COLORS.preinventario.fg }}>Preinventario</span>
                {c.current_state === "preinventario" || c.current_state === "en_inventario" ? (
                  <>Ficha <span className="font-mono font-semibold">{c.preinventory_code || "—"}</span>{c.current_state === "en_inventario" ? " · ya está en Inventario" : " · en preparación"}</>
                ) : (
                  <>Cuando el equipo quede listo, dalo de alta para limpieza, fotos, costos y precios.</>
                )}
              </div>
              {c.current_state === "preinventario" && c.preinventory_item_id ? (
                <Button size="sm" variant="outline" onClick={() => navigate(`/preinventario?item=${c.preinventory_item_id}`)} className="gap-1.5" data-testid="open-preinventory-item">
                  <Package size={13} weight="bold" /> Abrir ficha
                </Button>
              ) : c.current_state === "en_inventario" && c.product_id ? (
                <Button size="sm" variant="outline" onClick={() => navigate("/products")} className="gap-1.5" data-testid="open-inventory-product">
                  <Package size={13} weight="bold" /> Ver en Inventario
                </Button>
              ) : !["vendido", "donadora", "baja_definitiva"].includes(c.current_state) && (
                <Button size="sm" variant="outline" onClick={dischargeToPreinventory} disabled={busy} className="gap-1.5 border-[#0e7490] text-[#0e7490] hover:bg-[#0e7490] hover:text-white" data-testid="discharge-to-preinventory">
                  <ArrowRight size={13} weight="bold" /> Dar de alta → Preinventario
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────── Labels editor ────────────────────────────────────
const LABEL_PALETTE = [
  "#E0000F", "#F97316", "#EAB308", "#22C55E", "#0891B2",
  "#002FA7", "#7C3AED", "#DB2777", "#52525B", "#000000",
];

function LabelsEditor({ patient, onChanged }) {
  const [labels, setLabels] = useState(patient?.labels || []);
  const [draft, setDraft] = useState({ text: "", color: LABEL_PALETTE[0] });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { from_text, from_color, to_text, to_color }

  useEffect(() => { setLabels(patient?.labels || []); }, [patient?.id, patient?.labels]);
  const reloadCatalog = () => api.get("/patients/labels/catalog").then((r) => setCatalog(r.data || [])).catch(() => setCatalog([]));
  useEffect(() => { reloadCatalog(); }, [patient?.id]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}/labels`, { labels: next });
      setLabels(next);
      onChanged?.();
      reloadCatalog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la etiqueta");
    } finally {
      setSaving(false);
    }
  };

  const addLabel = () => {
    const text = draft.text.trim();
    if (!text) { toast.error("Escribe un texto"); return; }
    if (labels.some((l) => l.text === text && (l.color || "") === (draft.color || ""))) {
      toast.info("Ya tiene esa etiqueta"); return;
    }
    persist([...labels, { text, color: draft.color }]);
    setDraft({ text: "", color: draft.color });
  };
  const removeLabel = (idx) => persist(labels.filter((_, i) => i !== idx));

  const saveEdit = async () => {
    const text = (editing.to_text || "").trim();
    if (!text) { toast.error("El texto no puede estar vacío"); return; }
    try {
      await api.patch("/patients/labels/catalog", {
        from_text: editing.from_text,
        from_color: editing.from_color,
        to_text: text,
        to_color: editing.to_color,
      });
      toast.success("Etiqueta actualizada en todas las tarjetas");
      setEditing(null);
      reloadCatalog();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const deleteFromCatalog = async (c) => {
    if (!window.confirm(`Eliminar "${c.text}" de todas las tarjetas donde aparece (${c.count})?`)) return;
    try {
      await api.post("/patients/labels/catalog/delete", { text: c.text, color: c.color });
      toast.success("Etiqueta eliminada del catálogo");
      reloadCatalog();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  // Catálogo: etiquetas que aún NO están en el paciente.
  const availableFromCatalog = catalog.filter(
    (c) => !labels.some((l) => l.text === c.text && (l.color || "") === (c.color || ""))
  );

  return (
    <section data-testid="patient-labels-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Etiquetas</div>
        {catalog.length > 0 && (
          <button
            type="button"
            onClick={() => setManageOpen((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
            data-testid="manage-catalog-toggle"
          >
            <PencilSimple size={11} weight="bold" />
            {manageOpen ? "Cerrar" : "Gestionar catálogo"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {labels.length === 0 && (
          <span className="text-xs text-zinc-400 italic">Sin etiquetas. Añade una para identificar rápido este equipo.</span>
        )}
        {labels.map((lb, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white"
            style={{ background: lb.color || "#52525B" }}
            data-testid={`patient-label-${i}`}
          >
            {lb.text}
            <button
              type="button"
              onClick={() => removeLabel(i)}
              disabled={saving}
              className="hover:bg-black/20 rounded-full p-0.5 -mr-1"
              aria-label={`Quitar etiqueta ${lb.text}`}
              data-testid={`patient-label-rm-${i}`}
            >
              <X size={9} weight="bold" />
            </button>
          </span>
        ))}
      </div>

      {manageOpen && (
        <div className="border border-zinc-200 rounded-md p-3 mb-3 bg-zinc-50/50" data-testid="manage-catalog-panel">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
            Catálogo global — editar o eliminar afecta todas las tarjetas
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {catalog.map((c, i) => {
              const key = `${c.text}::${c.color}`;
              const isEditing = editing && editing.from_text === c.text && editing.from_color === c.color;
              if (isEditing) {
                return (
                  <div key={key} className="flex flex-wrap items-center gap-1.5 p-2 rounded-sm bg-white border border-[#002FA7]/40">
                    <Input
                      value={editing.to_text}
                      onChange={(e) => setEditing({ ...editing, to_text: e.target.value })}
                      className="h-7 max-w-[140px] text-xs"
                      data-testid={`edit-label-text-${i}`}
                    />
                    <div className="flex gap-0.5">
                      {LABEL_PALETTE.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditing({ ...editing, to_color: color })}
                          className={`w-5 h-5 rounded-sm border transition-all ${editing.to_color === color ? "border-zinc-900 scale-110" : "border-transparent"}`}
                          style={{ background: color }}
                          data-testid={`edit-label-color-${color}`}
                        />
                      ))}
                    </div>
                    <Button size="sm" onClick={saveEdit} className="h-7 bg-[#002FA7] hover:bg-[#0043c2] text-xs" data-testid={`edit-label-save-${i}`}>
                      Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="h-7 text-xs">
                      Cancelar
                    </Button>
                  </div>
                );
              }
              return (
                <div key={key} className="flex items-center justify-between gap-2 p-1.5 rounded-sm hover:bg-white transition-colors group">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white truncate"
                      style={{ background: c.color || "#52525B" }}
                    >
                      {c.text}
                    </span>
                    <span className="text-[10px] text-zinc-400 font-mono-data">{c.count} tarjeta{c.count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setEditing({ from_text: c.text, from_color: c.color, to_text: c.text, to_color: c.color })}
                      className="text-zinc-500 hover:text-[#002FA7] p-1"
                      aria-label="Editar etiqueta"
                      data-testid={`edit-label-btn-${i}`}
                    >
                      <PencilSimple size={12} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteFromCatalog(c)}
                      className="text-zinc-500 hover:text-[#E0000F] p-1"
                      aria-label="Eliminar etiqueta"
                      data-testid={`delete-label-btn-${i}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {availableFromCatalog.length > 0 && !manageOpen && (
        <div className="mb-2">
          <div className="text-[10px] text-zinc-500 mb-1">Usadas en otras tarjetas — click para agregar:</div>
          <div className="flex flex-wrap gap-1.5">
            {availableFromCatalog.map((c, i) => (
              <button
                type="button"
                key={i}
                onClick={() => persist([...labels, { text: c.text, color: c.color }])}
                disabled={saving}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white opacity-70 hover:opacity-100 transition-opacity"
                style={{ background: c.color || "#52525B" }}
                data-testid={`catalog-label-${i}`}
              >
                <Plus size={9} weight="bold" /> {c.text}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          value={draft.text}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLabel(); } }}
          placeholder="Nueva etiqueta…"
          className="h-8 max-w-xs"
          data-testid="patient-label-input"
        />
        <div className="flex gap-1">
          {LABEL_PALETTE.map((color) => (
            <button
              type="button"
              key={color}
              onClick={() => setDraft({ ...draft, color })}
              className={`w-6 h-6 rounded-sm border-2 transition-all ${draft.color === color ? "border-zinc-900 scale-110" : "border-transparent hover:border-zinc-300"}`}
              style={{ background: color }}
              aria-label={`Color ${color}`}
              data-testid={`patient-label-color-${color}`}
            />
          ))}
        </div>
        <Button onClick={addLabel} disabled={!draft.text.trim() || saving} size="sm" className="gap-1 bg-[#002FA7] hover:bg-[#0043c2]" data-testid="patient-label-add">
          <Plus size={12} weight="bold" /> Crear
        </Button>
      </div>
    </section>
  );
}

// ─────────────────────── Quick-add popover en la tarjeta ──────────────────